import React, { useEffect, useState } from 'react';
import { NButton, NDialog, NInput } from '@innersource-nn/novoui-react';
import useDatasetEdit from 'views/Datasets/hooks/useDatasetEdit';
import { DatasetEditProps } from 'types/Forms';
import { ModalProps } from 'types/Modal';
import { Dataset } from 'types/Dataset';
import NInputWithChips from 'components/Inputs/Labels Input/NInputWithChips';
import NInputTooltip from 'components/Inputs/InputsWithTooltip/NInputTooltip';
import NCheckboxTooltip from 'components/Inputs/InputsWithTooltip/NCheckboxTooltip';
import NTextAreaQuill from 'components/Inputs/TextAreaQuill/NTextAreaQuill';
import NSelectTooltip from 'components/Inputs/InputsWithTooltip/NSelectTooltip';
import DatasetClassificationTooltip, {
    classificationsOptions,
} from '../DatasetClassification';
import DatasetVisibilityTooltip, {
    visibilityOptions,
} from '../DatasetVisibility';
export interface EditDatasetProps extends ModalProps {
    dataset: Dataset;
}

export default function EditDataset({
    dataset,
    visible,
    onClose,
}: EditDatasetProps) {
    const [formData, setFormData] = useState<DatasetEditProps>({
        label: dataset.label,
        description: dataset.description,
        tags: dataset.tags,
        businessOwnerEmail: dataset.businessOwnerEmail,
        businessOwnerDelegationEmails: dataset.businessOwnerDelegationEmails,
        enableCrawler: dataset.enableCrawler,
        enableReadTrail: dataset.enableReadTrail,
        datasetStageOptions: dataset.datasetStageOptions,
        confidentiality: dataset.confidentiality,
        visibility: dataset.visibility,
    });

    useEffect(() => {
        setFormData({
            label: dataset.label,
            description: dataset.description,
            tags: dataset.tags,
            businessOwnerEmail: dataset.businessOwnerEmail,
            businessOwnerDelegationEmails:
                dataset.businessOwnerDelegationEmails,
            enableCrawler: dataset.enableCrawler,
            enableReadTrail: dataset.enableReadTrail,
            datasetStageOptions: dataset.datasetStageOptions,
            confidentiality: dataset.confidentiality,
            visibility: dataset.visibility,
        });
    }, [dataset]);

    const { loading, submitDatasetChanges } = useDatasetEdit({
        datasetUri: dataset.datasetUri,
    });

    const updateLabels = (inputName: string, newValue: string[]) => {
        setFormData((prevData: any) => {
            return {
                ...prevData,
                [inputName]: newValue,
            };
        });
    };

    const handleOwnerInitials = () => {
        let newValue = formData.businessOwnerEmail;
        if (!newValue.includes('@') && newValue !== '') {
            newValue += '@novonordisk.com';
        }

        setFormData((prevData: DatasetEditProps) => {
            return {
                ...prevData,
                businessOwnerEmail: newValue,
            };
        });
    };

    function handleClassificationChange(
        event: React.ChangeEvent<HTMLSelectElement>,
    ) {
        const { value } = event.target;
        setFormData((prevData: any) => {
            return {
                ...prevData,
                confidentiality: value,
                visibility: value === 'C4' ? 'Private' : prevData.visibility,
            };
        });
    }

    return (
        visible &&
        onClose && (
            <NDialog
                isVisible={visible}
                closeDialog={onClose}
                backgroundClose
                headline="Edit Dataset"
                size="large"
                showDividers={false}
            >
                <div className="flex flex-col gap-12 mt-8">
                    <div className="grid grid-cols-2 gap-16">
                        <NInput
                            label="Dataset name"
                            size="medium"
                            className="mt-4"
                            variant="outline"
                            placeholder="Maximum 52 characters..."
                            modelValue={formData.label}
                            invalid={formData.label.length > 52}
                            assistiveText={
                                formData.label.length > 52
                                    ? 'Maximum 52 characters are allowed'
                                    : ''
                            }
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    label: e.target.value,
                                })
                            }
                        />
                        <NInputTooltip
                            content="Designate the business owner who holds primary responsiblity for the dataset."
                            label="Business Owner"
                            size="medium"
                            variant="outline"
                            placeholder="Use NN initials"
                            modelValue={formData.businessOwnerEmail}
                            onBlur={handleOwnerInitials}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    businessOwnerEmail: e.target.value,
                                })
                            }
                        />
                    </div>
                    <NTextAreaQuill
                        label="Dataset description"
                        variant="outline"
                        placeholder="Please insert a short description which describes what this dataset contains and the purpose of the dataset..."
                        modelValue={formData.description}
                        onChange={(value) =>
                            setFormData({
                                ...formData,
                                description: value,
                            })
                        }
                    />
                    <div className="grid grid-cols-2 gap-16">
                        <NSelectTooltip
                            tooltipChildren={<DatasetClassificationTooltip />}
                            label="Data Classification"
                            size="medium"
                            variant="outline"
                            name="confidentiality"
                            options={classificationsOptions}
                            modelValue={formData.confidentiality}
                            onChange={handleClassificationChange}
                            placeholder="Select data classification"
                        />
                        <NSelectTooltip
                            tooltipChildren={<DatasetVisibilityTooltip />}
                            label="Visibility"
                            size="medium"
                            variant="outline"
                            options={visibilityOptions}
                            disabled={formData.confidentiality === 'C4'}
                            modelValue={formData.visibility}
                            onChange={(event) => {
                                const { value } = event.target;
                                setFormData({
                                    ...formData,
                                    visibility: value,
                                });
                            }}
                        />
                    </div>

                    <NInputWithChips
                        content="Designate data stewards who hold responsiblity for the dataset access management."
                        name="businessOwnerDelegationEmails"
                        modelValue=""
                        label="Add Stewards responsible for dataset"
                        placeholder="Type and press enter... (Use NN initials)"
                        updateLabels={updateLabels}
                        labels={formData.businessOwnerDelegationEmails}
                    />
                    <div className="flex gap-32 items-center justify-start">
                        <NCheckboxTooltip
                            content="Select to enable automated crawling of data source to populate and update the dataset."
                            label="Enable Crawler"
                            name="enableCrawler"
                            checked={formData.enableCrawler}
                            checkboxSize="medium"
                            onChange={() =>
                                setFormData({
                                    ...formData,
                                    enableCrawler: !formData.enableCrawler,
                                })
                            }
                        />
                        <NCheckboxTooltip
                            content="Enable this setting allows AWS CloudTrail to track and log all read access to files in your S3 bucket. This ensures compliance with GDPR by providing detailed records of who has accessed your data."
                            label="Enable Read Logs Tracking"
                            name="enableReadTrail"
                            checked={formData.enableReadTrail}
                            checkboxSize="medium"
                            onChange={() =>
                                setFormData({
                                    ...formData,
                                    enableReadTrail: !formData.enableReadTrail,
                                })
                            }
                        />
                        <NCheckboxTooltip
                            content="Select to enable this dataset to be deployed to all the accounts within the environment the dataset belongs to."
                            label="Deploy to all accounts"
                            name="datasetStageOptions"
                            checkboxSize="medium"
                            checked={formData.datasetStageOptions !== 'PROD'}
                            onChange={() =>
                                setFormData({
                                    ...formData,
                                    datasetStageOptions:
                                        formData.datasetStageOptions === 'PROD'
                                            ? 'ALL'
                                            : 'PROD',
                                })
                            }
                        />
                    </div>
                    <div className="flex justify-end gap-8">
                        <NButton variant="outline" square onClick={onClose}>
                            Cancel
                        </NButton>
                        <NButton
                            variant="fill"
                            isLoading={loading}
                            square
                            className="bg-sea-blue-600"
                            onClick={() =>
                                submitDatasetChanges(formData, onClose)
                            }
                        >
                            Update
                        </NButton>
                    </div>
                </div>
            </NDialog>
        )
    );
}
